'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

// Set by /admin/login/link when a sign-in link could not be redeemed.
const LINK_ERRORS: Record<string, string> = {
  expired: 'That sign-in link has expired. They last 15 minutes — here is a fresh one.',
  used: 'That sign-in link has already been used. Request another below.',
  invalid: 'That sign-in link is not valid. Request another below.',
};

const FIELD_STYLE: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '14px 16px',
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-mono)',
  fontSize: 13,
};
const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  marginBottom: 8,
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

export function AdminLoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [stage, setStage] = useState<'pw' | 'totp' | 'recovery'>('pw');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Link-first sign-in. The password form stays available underneath so a mail
  // outage cannot lock the only admin out of their own dashboard.
  const [usePassword, setUsePassword] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  useEffect(() => {
    const reason = searchParams.get('link');
    if (reason && LINK_ERRORS[reason]) setError(LINK_ERRORS[reason]);
  }, [searchParams]);

  async function sendLink(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = (await res.json()) as { ok?: boolean; delivered?: boolean; reason?: string };
      if (!res.ok) throw new Error('Could not send the link');
      // `delivered: false` only comes back for the admin's own address, so it is
      // safe to show and saves waiting on mail that was refused.
      if (data.delivered === false) {
        setError(`Could not send the link: ${data.reason ?? 'unknown error'}. Use your password below.`);
        return;
      }
      setLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the link');
    } finally {
      setBusy(false);
    }
  }

  async function submitLogin(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json()) as { ok?: boolean; requiresTotp?: boolean; requiresSetup?: boolean; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Login failed');
      if (data.requiresSetup) {
        router.push('/admin/login/setup');
        return;
      }
      if (data.requiresTotp) {
        setStage('totp');
        return;
      }
      router.push('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const endpoint = stage === 'recovery' ? '/api/admin/auth/recovery' : '/api/admin/auth/totp';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, trustDevice }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Verification failed');
      router.push('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'totp' || stage === 'recovery') {
    return (
      <form onSubmit={submitTotp} className="flex flex-col gap-5">
        <div>
          <label htmlFor="code" style={LABEL_STYLE}>
            {stage === 'recovery' ? 'Recovery code' : 'Authenticator code'}
          </label>
          <input
            id="code"
            type="text"
            inputMode={stage === 'totp' ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            placeholder={stage === 'recovery' ? 'XXXX-XXXX-XXXX' : '123 456'}
            className="contact-field"
            style={FIELD_STYLE}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
          />
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
          />
          <span className="font-mono text-text-muted" style={{ fontSize: 12 }}>
            Trust this device for 30 days
          </span>
        </label>
        {error && (
          <p className="font-mono" style={{ color: 'var(--rose)', fontSize: 12 }}>{error}</p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="cta-primary inline-flex items-center justify-center disabled:opacity-50"
          style={{
            background: 'linear-gradient(135deg, var(--accent), var(--indigo))',
            color: 'var(--bg-primary)',
            fontSize: 14,
            fontWeight: 800,
            padding: '13px 28px',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          {busy ? 'Verifying…' : 'Verify →'}
        </button>
        <button
          type="button"
          onClick={() => setStage(stage === 'recovery' ? 'totp' : 'recovery')}
          className="font-mono"
          style={{
            background: 'transparent',
            color: 'var(--text-muted)',
            fontSize: 12,
            letterSpacing: '0.04em',
            textAlign: 'center',
          }}
        >
          {stage === 'recovery' ? '← Back to authenticator' : 'Lost authenticator? Use a recovery code →'}
        </button>
      </form>
    );
  }

  if (linkSent) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-text" style={{ fontSize: 15, lineHeight: 1.6 }}>
          Check your inbox. If that address is the admin, a sign-in link is on its way.
        </p>
        <p className="font-mono text-text-muted" style={{ fontSize: 12, lineHeight: 1.7 }}>
          The link expires in 15 minutes and works once.
        </p>
        <button
          type="button"
          onClick={() => {
            setLinkSent(false);
            setError(null);
          }}
          className="font-mono self-start"
          style={{
            background: 'transparent',
            color: 'var(--text-muted)',
            fontSize: 12,
            letterSpacing: '0.04em',
          }}
        >
          ← Back
        </button>
      </div>
    );
  }

  if (!usePassword) {
    return (
      <form onSubmit={sendLink} className="flex flex-col gap-5">
        <div>
          <label htmlFor="magic-email" style={LABEL_STYLE}>Admin email</label>
          <input
            id="magic-email"
            type="email"
            autoComplete="email"
            className="contact-field"
            style={FIELD_STYLE}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>
        {error && <p className="font-mono" style={{ color: 'var(--rose)', fontSize: 12 }}>{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="cta-primary inline-flex items-center justify-center disabled:opacity-50"
          style={{
            background: 'linear-gradient(135deg, var(--accent), var(--indigo))',
            color: 'var(--bg-primary)',
            fontSize: 14,
            fontWeight: 800,
            padding: '13px 28px',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          {busy ? 'Sending…' : 'Email me a sign-in link →'}
        </button>
        <button
          type="button"
          onClick={() => {
            setUsePassword(true);
            setError(null);
          }}
          className="font-mono"
          style={{
            background: 'transparent',
            color: 'var(--text-muted)',
            fontSize: 12,
            letterSpacing: '0.04em',
            textAlign: 'center',
          }}
        >
          Use a password instead →
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submitLogin} className="flex flex-col gap-5">
      <div>
        <label htmlFor="email" style={LABEL_STYLE}>Admin email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          className="contact-field"
          style={FIELD_STYLE}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div>
        <label htmlFor="password" style={LABEL_STYLE}>Password</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          className="contact-field"
          style={FIELD_STYLE}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>
      {error && (
        <p className="font-mono" style={{ color: 'var(--rose)', fontSize: 12 }}>{error}</p>
      )}
      <button
        type="submit"
        disabled={busy}
        className="cta-primary inline-flex items-center justify-center disabled:opacity-50"
        style={{
          background: 'linear-gradient(135deg, var(--accent), var(--indigo))',
          color: 'var(--bg-primary)',
          fontSize: 14,
          fontWeight: 800,
          padding: '13px 28px',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        {busy ? 'Signing in…' : 'Continue →'}
      </button>
      <button
        type="button"
        onClick={() => {
          setUsePassword(false);
          setError(null);
        }}
        className="font-mono"
        style={{
          background: 'transparent',
          color: 'var(--text-muted)',
          fontSize: 12,
          letterSpacing: '0.04em',
          textAlign: 'center',
        }}
      >
        ← Email me a sign-in link instead
      </button>
    </form>
  );
}
